import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Phone,
  CheckCircle2,
  AlertTriangle,
  Factory,
  Activity,
  Eye,
  Gauge,
  Cpu,
  BarChart3,
  Layers3,
  ChevronDown,
  Menu,
  X,
} from "lucide-react";
import "./styles.css";

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, ease: "easeOut" as const },
  },
};

const stagger = {
  hidden: {},
  visible: {
    transition: { staggerChildren: 0.1 },
  },
};

const cardIn = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: "easeOut" as const },
  },
};

const companyName = "Auronix";
const phoneNumber = "6362325461";

const challenges = [
  {
    icon: AlertTriangle,
    title: "Frequent Machine Downtime",
    text: "Unexpected stoppages in embroidery, printing, and processing machines reduce production efficiency.",
  },
  {
    icon: Activity,
    title: "High Fabric & Material Wastage",
    text: "Defects, rework, and incorrect production settings increase fabric, thread, ink, and paper wastage.",
  },
  {
    icon: Factory,
    title: "Lack of Production Visibility",
    text: "Manual reporting and disconnected systems make it difficult to track production performance in real time.",
  },
  {
    icon: Gauge,
    title: "Low Operational Efficiency",
    text: "Idle machines, inefficient scheduling, and production bottlenecks reduce throughput and profitability.",
  },
  {
    icon: Eye,
    title: "Quality & Traceability Issues",
    text: "Without digital tracking, identifying defect sources and maintaining quality consistency becomes difficult.",
  },
  {
    icon: Cpu,
    title: "Reactive Maintenance",
    text: "Most factories rely on reactive maintenance instead of predictive maintenance and machine health monitoring.",
  },
  {
    icon: BarChart3,
    title: "Poor Inventory & Production Planning",
    text: "Untracked inventory and inaccurate production planning lead to delays, stock issues, and excess costs.",
  },
  {
    icon: Layers3,
    title: "Rising Energy & Utility Costs",
    text: "Inefficient machine operations and unmonitored energy consumption increase operational expenses.",
  },
];

const solutions = [
  "Connect embroidery, printing, calender, and production machines without replacing existing infrastructure",
  "Deploy real-time production monitoring and machine analytics dashboards",
  "Track fabric movement, material usage, and production status across departments",
  "Use AI-driven predictive maintenance to reduce unexpected machine failures",
  "Implement quality monitoring and defect traceability systems",
  "Monitor downtime, throughput, operator efficiency, and machine utilization",
  "Integrate with ERP, inventory, and production management systems",
  "Build scalable smart textile manufacturing systems with minimal disruption",
];

const CARD_H = 88;
const CARD_GAP = 12;
const CARD_STEP = CARD_H + CARD_GAP;

const RollingSolutions = () => {
  const n = solutions.length;
  const [offset, setOffset] = useState(0);
  const [sliding, setSliding] = useState(false);

  useEffect(() => {
    const id = setInterval(() => {
      setSliding(true);
      setOffset((o) => o + 1);
    }, 3500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (offset === n) {
      const t = setTimeout(() => {
        setSliding(false);
        setOffset(0);
      }, 520);
      return () => clearTimeout(t);
    }
  }, [offset, n]);

  const extended = [...solutions, solutions[0], solutions[1], solutions[2]];

  return (
    <div className="photo-reel" style={{ height: 3 * CARD_H + 2 * CARD_GAP }}>
      <div
        className="photo-reel__track"
        style={{
          transform: `translateY(-${offset * CARD_STEP}px)`,
          transition: sliding ? "transform 0.5s cubic-bezier(0.4,0,0.2,1)" : "none",
        }}
      >
        {extended.map((item, i) => {
          const isActive = i === offset + 1;
          return (
            <div
              key={i}
              className={`photo-reel__card${isActive ? " photo-reel__card--active" : ""}`}
            >
              <CheckCircle2
                size={isActive ? 20 : 17}
                className="photo-reel__icon"
              />
              <span>{item}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const TextileIndustryPage = () => {
  const [industriesOpen, setIndustriesOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const industriesRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (industriesRef.current && !industriesRef.current.contains(e.target as Node)) {
        setIndustriesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <main className="site-shell">
      {/* ── Header ── */}
      <nav className="nav" aria-label="Main navigation">
        <a className="brand" href="/" aria-label={`${companyName} home`}>
          <span className="brand__mark">AX</span>

          <span>
            {companyName}
            <small>Industrial Intelligence</small>
          </span>
        </a>

        <div className="nav__links">
          <a href="/#platform">Platform</a>
          <a href="/#solutions">Solutions</a>
          <a href="/#impact">Impact</a>
          <div
            className={`nav__dropdown${industriesOpen ? " nav__dropdown--open" : ""}`}
            ref={industriesRef}
          >
            <button
              className="nav__dropdown-trigger"
              onClick={() => setIndustriesOpen((o) => !o)}
              aria-haspopup="listbox"
              aria-expanded={industriesOpen}
            >
              Industries <ChevronDown size={15} />
            </button>
            <ul className="nav__dropdown-menu" role="listbox">
              <li>
                <Link to="/industries/automotive" onClick={() => setIndustriesOpen(false)}>
                  Automotive Part Manufacturing
                </Link>
              </li>
              <li>
                <Link to="/industries/textile" onClick={() => setIndustriesOpen(false)}>
                  Textile Industry
                </Link>
              </li>
            </ul>
          </div>
        </div>
        <button
          className="nav__hamburger"
          onClick={() => setMobileMenuOpen((o) => !o)}
          aria-label={mobileMenuOpen ? "Close menu" : "Open menu"}
          aria-expanded={mobileMenuOpen}
        >
          {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
        </button>
      </nav>

      {/* ── Mobile nav ── */}
      {mobileMenuOpen && (
        <div className="mobile-nav">
          <a href="/#platform" onClick={() => setMobileMenuOpen(false)}>Platform</a>
          <a href="/#solutions" onClick={() => setMobileMenuOpen(false)}>Solutions</a>
          <a href="/#impact" onClick={() => setMobileMenuOpen(false)}>Impact</a>
          <div className="mobile-nav__divider" />
          <p className="mobile-nav__group-label">Industries</p>
          <Link to="/industries/automotive" className="mobile-nav__sub" onClick={() => setMobileMenuOpen(false)}>
            Automotive Part Manufacturing
          </Link>
          <Link to="/industries/textile" className="mobile-nav__sub" onClick={() => setMobileMenuOpen(false)}>
            Textile Industry
          </Link>
        </div>
      )}

      {/* ── Hero ── */}
      <section
        className="industry-hero"
        style={{
          background:
            "linear-gradient(135deg, rgba(230,252,253,0.9) 0%, #fff 60%)",
          padding: "90px max(24px, calc((100vw - 1200px) / 2)) 70px",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <motion.div
          initial="hidden"
          animate="visible"
          variants={stagger}
        >
          <motion.p
            className="eyebrow eyebrow--dark"
            variants={fadeUp}
          >
            Industry Solutions
          </motion.p>

          <motion.h1
            variants={fadeUp}
            style={{ maxWidth: 850 }}
          >
            Textile Manufacturing
            <span> Powered by Industrial AI.</span>
          </motion.h1>

          <motion.p
            variants={fadeUp}
            style={{
              maxWidth: 700,
              fontSize: "clamp(1.05rem, 1.3vw, 1.35rem)",
              color: "var(--muted)",
              lineHeight: 1.6,
              marginBottom: 34,
            }}
          >
            We help textile manufacturers improve production visibility,
            reduce downtime, optimize material usage, and build
            intelligent data-driven factories using Industrial IoT,
            AI analytics, and real-time monitoring systems.
          </motion.p>

          <motion.div
            className="hero__actions"
            variants={fadeUp}
          >
            <a className="button button--primary" href="#contact">
              Book a factory study
            </a>

            <a className="button button--ghost" href="#challenges">
              Explore Challenges
            </a>
          </motion.div>
        </motion.div>
      </section>

      {/* ── Challenges ── */}
      <section
        className="section"
        id="challenges"
        style={{
          padding: "90px max(24px, calc((100vw - 1200px) / 2))",
        }}
      >
        <motion.div
          className="section__intro"
          style={{ textAlign: "center" }}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-80px" }}
          variants={fadeUp}
        >
          <p className="eyebrow eyebrow--dark">
            Industry Challenges
          </p>

          <h2>
            Key Challenges Textile Manufacturers Face
          </h2>

          <p
            style={{
              maxWidth: 720,
              color: "var(--muted)",
              margin: "0 auto",
              textAlign: "center",
            }}
          >
            Textile factories operate under constant pressure to improve
            quality, reduce production losses, optimize machine
            utilization, and deliver orders on time while managing
            operational costs.
          </p>
        </motion.div>

        <motion.div
          className="product-grid"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-60px" }}
          variants={stagger}
        >
          {challenges.map(({ icon: Icon, title, text }) => (
            <motion.article
              className="product"
              key={title}
              variants={cardIn}
              whileHover={{
                y: -6,
                transition: { duration: 0.22 },
              }}
            >
              <div
                className="product__body"
                style={{ padding: "2rem" }}
              >
                <span className="product__icon">
                  <Icon size={22} />
                </span>

                <h3>{title}</h3>

                <p style={{ color: "var(--muted)" }}>
                  {text}
                </p>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </section>

      {/* ── How We Help ── */}
      <section className="impact" id="solutions">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: false, margin: "-80px" }}
          variants={fadeUp}
          style={{ textAlign: "center" }}
        >
          <p className="eyebrow eyebrow--dark">
            How We Help
          </p>

          <h2>
            How Auronix Transforms Textile Factories
          </h2>

          <p
            style={{
              color: "var(--muted)",
              maxWidth: 720,
              margin: "0 auto",
              textAlign: "center",
            }}
          >
            We combine Industrial IoT, AI analytics, production
            monitoring, and machine intelligence to help textile
            manufacturers improve efficiency, quality, traceability,
            and profitability.
          </p>
        </motion.div>

        <RollingSolutions />
      </section>

      {/* ── Full-width factory image ── */}
      <motion.div
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, margin: "-80px" }}
        variants={fadeUp}
        style={{
          width: "100%",
          overflow: "hidden",
          borderTop: "1px solid var(--line)",
          borderBottom: "1px solid var(--line)",
        }}
      >
        <img
          src="/image2.png"
          alt="Smart textile factory monitoring"
          style={{
            width: "100%",
            height: "auto",
            display: "block",
          }}
        />
      </motion.div>

      {/* ── CTA / Footer ── */}
      <motion.section
        className="contact"
        id="contact"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: false, margin: "-80px" }}
        variants={stagger}
      >
        <motion.div variants={fadeUp}>
          <p className="eyebrow eyebrow--dark">
            Get Started
          </p>

          <h2>
            Ready to build a smarter textile factory?
          </h2>

          <p
            style={{
              color: "var(--muted)",
              maxWidth: 560,
            }}
          >
            Share your production setup, downtime issues, or quality
            challenges. We’ll help you create a practical roadmap for
            smart textile manufacturing transformation.
          </p>
        </motion.div>

        <motion.div
          className="contact__actions"
          variants={fadeUp}
        >
          <a
            className="button button--light"
            href={`tel:+91${phoneNumber}`}
          >
            <Phone size={18} />
            {phoneNumber}
          </a>

          <a
            className="button button--primary"
            href="mailto:hello@auronix.ai"
          >
            Contact Us
          </a>
        </motion.div>
      </motion.section>
    </main>
  );
};

export default TextileIndustryPage;